import { Button, TableItem } from 'components/atoms';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiNavigationArrow } from 'react-icons/pi';
import { ILabelReportDetailSalesChannelRow } from 'services/label-report/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { ILabelReportFormValues } from '../LabelReportForm';

const goToSalesChannel = (id: string) =>
  window.open(`/settings/sales-channel/${id}/providers`, '_blank', 'noopener,noreferrer');

enum SORT_COLUMNS {
  salesChannelName,
  divisionPercentage,
  amountEarned,
  amountLabelEarned,
  actions,
}

interface TableNode extends ILabelReportDetailSalesChannelRow {
  id: string;
}

const LabelReportGroupedSalesChannelList: React.FC = () => {
  const { values } = useFormikContext<ILabelReportFormValues>();
  const { t } = useTranslation();
  const columnsVisibility = useTableColumnsVisibility(
    'LabelReportGroupedSalesChannelList',
    SORT_COLUMNS
  );

  const items: ILabelReportDetailSalesChannelRow[] =
    values?.groupedSalesChannelList ?? [];
  const tableData: TableNode[] = items.map((row, index) => ({
    ...row,
    id: row.salesChannelId ?? `row-${index}`,
  }));

  return (
    <Table
      id={columnsVisibility.tableId}
      data={{ nodes: tableData }}
      columns={[
        {
          label: t('input.label.salesChannelName'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text text={item.salesChannelName} />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.salesChannelName),
        },
        {
          label: t('input.label.amountEarned'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Currency
                text={formatCurrency(
                  item.amountEarned != null ? Number(item.amountEarned) : 0
                )}
              />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.amountEarned),
        },
        {
          label: t('input.label.amountLabelEarned'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Currency
                text={formatCurrency(
                  item.amountLabelEarned != null
                    ? Number(item.amountLabelEarned)
                    : 0
                )}
              />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.amountLabelEarned),
        },
        {
          label: t('input.label.divisionPercentage'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text
                text={
                  item.divisionPercentage != null
                    ? Number(item.divisionPercentage).toFixed(2)
                    : ''
                }
              />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.divisionPercentage),
        },
        {
          label: '',
          renderCell: (item: TableNode) => (
            <TableItem justifyPosition='end'>
              <Button
                isClear
                onlyIcon
                onClick={() => goToSalesChannel(item.salesChannelId)}>
                                <Button.Description description={t('button.open')} />

                                <Button.Icon
                  icon={
                    <PiNavigationArrow transform='scale(-1,1)' />
                  }
                />
              </Button>
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.actions),
        },
      ]}
    />
  );
};

export default LabelReportGroupedSalesChannelList;
